// Loop levers (UAT-74/80/101/102, founder 10-11): the priced levers a commitment's own source rows
// offer, and the typed overrides a reader may put on one. A lever's money travels only as a figure
// key into its stage's figures; this file carries identity, words and the override shape.
// P4 (systems-of-record rebuild): a lever also carries the dates its money counts from, its share
// of the shortfall waiting on it, what it saves in 2026 and the recorded options that move it.

import type { SynergyLoopSegment } from './synergy-loop.v1';

/**
 * A lever's typed outcome against today's pace landing (round 2, #10234): it closes the whole
 * year-end shortfall IF it happens, narrows it IF it happens, or is the gate other levers wait on.
 * There is no flat "meets" outcome for a lever: every lever is a condition.
 */
export const SYNERGY_LOOP_LEVER_OUTCOMES = ['closes_gap_if', 'narrows_gap_if', 'gate'] as const;
export type SynergyLoopLeverOutcome = (typeof SYNERGY_LOOP_LEVER_OUTCOMES)[number];
/** What can still be delivered: flat "meets" only when today's pace already meets the promise. */
export const SYNERGY_LOOP_DELIVERABLE_OUTCOMES =
  ['meets_promise', 'meets_promise_if', 'short_of_promise_if'] as const;
export type SynergyLoopDeliverableOutcome = (typeof SYNERGY_LOOP_DELIVERABLE_OUTCOMES)[number];

/** A lever's override: move its date, or (role exits) take only the largest N open roles. */
export const SYNERGY_LOOP_LEVER_OVERRIDES = ['lever_date', 'lever_count'] as const;
export type SynergyLoopLeverOverrideKind = (typeof SYNERGY_LOOP_LEVER_OVERRIDES)[number];

/**
 * What a recorded option does to the cause it answers (DESIGN section 7, founder rule 4): move
 * its date, route around it, replace the value, clear the cause or find out. Never an "ask" or
 * a "confirm": an option is work, not a request for a date.
 */
export const SYNERGY_LOOP_LEVER_OPTION_KINDS =
  ['move_a_date', 'route_around', 'replace_value', 'clear_cause', 'find_out'] as const;
export type SynergyLoopLeverOptionKind = (typeof SYNERGY_LOOP_LEVER_OPTION_KINDS)[number];

/** One dated release a lever's run-rate counts from: the people's last day, then the next month. */
export type SynergyLoopLeverRelease = Readonly<{
  /** ISO day of the people's last day (the exit task's finish). */
  last: string;
  /** The month ('YYYY-MM') the released run-rate counts from: the month after the last day. */
  countsFrom: string;
  /** The figure key of the run-rate released. */
  amountKey: string;
  /** People released, when the source counts them (absent for contract retirements). */
  people?: number;
}>;

/** A recorded option on a lever, priced by the engine; money only as figure keys. */
export type SynergyLoopLeverOption = Readonly<{
  /** Stable option identity in the register (never shown). */
  ref: string;
  kind: SynergyLoopLeverOptionKind;
  /** Who proposed it and on what day, as the register records them. */
  proposer?: string;
  proposedOn?: string;
  /** The figure keys of the run-rate it adds at year end and of what it saves in 2026. */
  runRateKey: string;
  savingsKey: string;
  /** The server's sentence for the option. */
  line: readonly SynergyLoopSegment[];
}>;

export type SynergyLoopLever = Readonly<{
  /** Stable lever identity (a source reference), used by an override. */
  ref: string;
  label: string;
  owner: string | null;
  /** The figure key of the part of the year-end shortfall this lever closes. */
  closesKey: string;
  /** ISO day the lever lands on: the data's date, or the reader's override. */
  by: string;
  /** The override kinds this lever accepts. */
  overrides: readonly SynergyLoopLeverOverrideKind[];
  /** Open roles counted in the lever (role exits only), and how many the data holds. */
  count?: Readonly<{ used: number; open: number }>;
  outcome: SynergyLoopLeverOutcome;
  /** True when other levers on this commitment wait on this one (a dependency). */
  gate: boolean;
  /** The gate levers this one waits on, by ref. */
  dependsOn?: readonly string[];
  /** The server's sentence for this lever; money only as figure keys. */
  line: readonly SynergyLoopSegment[];
  /** Role exits only: the counted roles as the line states them ("21 role exits"), counts bound. */
  roles?: readonly SynergyLoopSegment[];
  /**
   * Role exits only: the exits tracker's own count beside the lever, as the line states it; a
   * different population, stated and never reconciled (beat 7 draws it as a footnote).
   */
  note?: readonly SynergyLoopSegment[];
  /**
   * Dated levers only. A gate: the figure key of its share of the shortfall, min(value waiting,
   * shortfall). Role exits: the part of their run-rate that waits on the gates.
   */
  waitingKey?: string;
  /** Role exits on dated releases: the figure key of what today's forecast already counts. */
  inForecastKey?: string;
  /** The figure key of what the lever saves in 2026. */
  savingsKey?: string;
  /** The dated releases its run-rate counts from, earliest first. */
  releases?: readonly SynergyLoopLeverRelease[];
  /** The server's sentence on when its money counts; money only as figure keys. */
  timing?: readonly SynergyLoopSegment[];
  /** The recorded options that move its date or work around it, each priced. */
  options?: readonly SynergyLoopLeverOption[];
}>;

export type SynergyLoopLeverOverride =
  | Readonly<{ kind: 'lever_date'; commitmentRef: string; leverRef: string; date: string }>
  | Readonly<{ kind: 'lever_count'; commitmentRef: string; leverRef: string; count: number }>;

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);
const isText = (value: unknown): value is string => typeof value === 'string' && value !== '';
const ISO_DAY = /^\d{4}-\d{2}-\d{2}$/u;
const ISO_MONTH = /^\d{4}-\d{2}$/u;
const isDay = (value: unknown): value is string => isText(value) && ISO_DAY.test(value);
const isCount = (value: unknown): value is number =>
  typeof value === 'number' && Number.isInteger(value) && value >= 0;

function readLeverCount(value: unknown): SynergyLoopLever['count'] | null | undefined {
  if (value === undefined) return undefined;
  return isRecord(value) && isCount(value.used) && isCount(value.open) && value.used <= value.open
    ? { used: value.used, open: value.open } : null;
}

const readEnum = <T extends string>(values: readonly T[], value: unknown): T | undefined =>
  values.find((entry) => entry === value);

/** Text and figure segments only; anything else drops the whole line. */
function readLine(value: unknown): SynergyLoopSegment[] | null {
  if (!Array.isArray(value)) return null;
  const line = value.map((segment): SynergyLoopSegment | null => {
    if (!isRecord(segment)) return null;
    if (segment.kind === 'text' && typeof segment.text === 'string')
      return { kind: 'text', text: segment.text };
    return segment.kind === 'figure' && isText(segment.key)
      ? { kind: 'figure', key: segment.key } : null;
  });
  return line.every((segment): segment is SynergyLoopSegment => segment !== null) ? line : null;
}

/** An optional run of segments: absent stays absent; one off the contract drops the lever. */
const readOptionalLine = (value: unknown): SynergyLoopSegment[] | null | undefined =>
  value === undefined ? undefined : readLine(value);

/** An optional figure key: absent stays absent; anything but text drops the lever. */
const readOptionalKey = (value: unknown): string | null | undefined =>
  value === undefined ? undefined : isText(value) ? value : null;

/** An optional list: absent stays absent; one entry off the contract drops the lever. */
function readOptionalList<T>(value: unknown,
  read: (entry: unknown) => T | null): T[] | null | undefined {
  if (value === undefined) return undefined;
  if (!Array.isArray(value)) return null;
  const entries = value.map(read);
  return entries.every((entry): entry is T => entry !== null) ? entries : null;
}

function readDependsOn(value: unknown): readonly string[] | null | undefined {
  if (value === undefined) return undefined;
  return Array.isArray(value) && value.every(isText) ? value : null;
}

function readRelease(value: unknown): SynergyLoopLeverRelease | null {
  if (!isRecord(value) || !isDay(value.last) || !isText(value.amountKey)) return null;
  if (!isText(value.countsFrom) || !ISO_MONTH.test(value.countsFrom)) return null;
  if (value.people !== undefined && !isCount(value.people)) return null;
  return { last: value.last, countsFrom: value.countsFrom, amountKey: value.amountKey,
    ...(value.people !== undefined ? { people: value.people } : {}) };
}

function readOption(value: unknown): SynergyLoopLeverOption | null {
  if (!isRecord(value) || !isText(value.ref)) return null;
  const kind = readEnum(SYNERGY_LOOP_LEVER_OPTION_KINDS, value.kind);
  const line = readLine(value.line);
  if (!kind || !line || !isText(value.runRateKey) || !isText(value.savingsKey)) return null;
  if (value.proposer !== undefined && !isText(value.proposer)) return null;
  if (value.proposedOn !== undefined && !isDay(value.proposedOn)) return null;
  return { ref: value.ref, kind,
    ...(value.proposer !== undefined ? { proposer: value.proposer } : {}),
    ...(value.proposedOn !== undefined ? { proposedOn: value.proposedOn } : {}),
    runRateKey: value.runRateKey, savingsKey: value.savingsKey, line };
}

type DatedFields = Pick<SynergyLoopLever, 'waitingKey' | 'inForecastKey' | 'savingsKey'
  | 'releases' | 'timing' | 'options'>;

/** The dated fields: each absent stays absent; one present but off the contract drops the lever. */
function readDatedFields(value: Record<string, unknown>): DatedFields | null {
  const read: Record<keyof DatedFields, unknown> = {
    waitingKey: readOptionalKey(value.waitingKey),
    inForecastKey: readOptionalKey(value.inForecastKey),
    savingsKey: readOptionalKey(value.savingsKey),
    releases: readOptionalList(value.releases, readRelease),
    timing: readOptionalLine(value.timing),
    options: readOptionalList(value.options, readOption),
  };
  const entries = Object.entries(read);
  if (entries.some(([, entry]) => entry === null)) return null;
  return Object.fromEntries(entries.filter(([, entry]) => entry !== undefined)) as DatedFields;
}

export const readSynergyLoopDeliverableOutcome = (value: unknown) =>
  readEnum(SYNERGY_LOOP_DELIVERABLE_OUTCOMES, value);

function readLever(value: unknown): SynergyLoopLever | null {
  if (!isRecord(value) || !isText(value.ref) || !isText(value.label)) return null;
  if (!isText(value.closesKey) || !isText(value.by) || !ISO_DAY.test(value.by)) return null;
  if (value.owner !== null && !isText(value.owner)) return null;
  const overrides = Array.isArray(value.overrides) ? value.overrides : null;
  const kinds = overrides?.filter((kind): kind is SynergyLoopLeverOverrideKind =>
    SYNERGY_LOOP_LEVER_OVERRIDES.includes(kind as SynergyLoopLeverOverrideKind));
  const count = readLeverCount(value.count);
  const outcome = readEnum(SYNERGY_LOOP_LEVER_OUTCOMES, value.outcome);
  const line = readLine(value.line);
  const dependsOn = readDependsOn(value.dependsOn);
  const [roles, note] = [readOptionalLine(value.roles), readOptionalLine(value.note)];
  const dated = readDatedFields(value);
  if (!kinds || kinds.length !== overrides!.length || count === null) return null;
  if (!outcome || !line || dependsOn === null || typeof value.gate !== 'boolean') return null;
  if (roles === null || note === null || dated === null) return null;
  return { ref: value.ref, label: value.label, owner: value.owner, closesKey: value.closesKey,
    by: value.by, overrides: kinds, ...(count ? { count } : {}), outcome, gate: value.gate,
    ...(dependsOn ? { dependsOn } : {}), line, ...(roles ? { roles } : {}),
    ...(note ? { note } : {}), ...dated };
}

/** A malformed lever list is omitted whole, never laid out partially. */
export function readSynergyLoopLevers(value: unknown): readonly SynergyLoopLever[] | undefined {
  if (!Array.isArray(value)) return undefined;
  const levers = value.map(readLever);
  return levers.every((lever): lever is SynergyLoopLever => lever !== null) ? levers : undefined;
}

/** Strict request reader for one lever override; null when it is off-contract. */
export function readSynergyLoopLeverOverride(
  value: Record<string, unknown>,
): SynergyLoopLeverOverride | null {
  const { kind, commitmentRef, leverRef } = value;
  if (!isText(commitmentRef) || !isText(leverRef)) return null;
  if (kind === 'lever_date')
    return isText(value.date) && ISO_DAY.test(value.date)
      ? { kind, commitmentRef, leverRef, date: value.date } : null;
  if (kind === 'lever_count')
    return isCount(value.count) && value.count > 0
      ? { kind, commitmentRef, leverRef, count: value.count } : null;
  return null;
}
